import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, PencilSimple, Trash, MagnifyingGlass, Upload, DownloadSimple, CurrencyDollar, Warning, Clock, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["Capsules", "Cream", "Gel", "Lotion", "Ointment/Paste", "Suspensions", "Topical", "Troche", "Suppository", "Veterinary", "Mouthwash"];

export default function AdminProductsPage() {
  const [products, setProducts] = useState([]);
  const [types, setTypes] = useState([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showCategoriesDialog, setShowCategoriesDialog] = useState(false);
  const [showHealthDialog, setShowHealthDialog] = useState(false);

  // Product form
  const [formType, setFormType] = useState("Capsules");
  const [formIngredient, setFormIngredient] = useState("");
  const [formStrength, setFormStrength] = useState("");
  const [formQty, setFormQty] = useState("");
  const [formQtyUnit, setFormQtyUnit] = useState("");
  const [formWsp, setFormWsp] = useState("");
  const [formRrp, setFormRrp] = useState("");
  const [formQuoteRequired, setFormQuoteRequired] = useState(false);
  const [formCategories, setFormCategories] = useState([]);

  // Filters
  const [filterCategory, setFilterCategory] = useState("all");

  // Bulk categorize
  const [showBulkCatDialog, setShowBulkCatDialog] = useState(false);
  const [bulkCatKey, setBulkCatKey] = useState("");
  const [bulkCatAction, setBulkCatAction] = useState("add");

  // Batch selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Bulk price
  const [bulkType, setBulkType] = useState("percentage");
  const [bulkValue, setBulkValue] = useState("");
  const [bulkApply, setBulkApply] = useState("both");

  // Import
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  // History
  const [history, setHistory] = useState([]);

  // Categories
  const [categories, setCategories] = useState([]);
  const [newCatLabel, setNewCatLabel] = useState("");

  // Pricing health
  const [health, setHealth] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products/types`, { withCredentials: true }).then(r => setTypes(r.data.types)).catch(() => {});
    axios.get(`${API}/categories`, { withCredentials: true }).then(r => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 50 });
    if (filterType !== "all") params.append("type", filterType);
    if (filterCategory !== "all") params.append("category", filterCategory);
    if (search) params.append("search", search);
    try {
      const { data } = await axios.get(`${API}/products?${params}`, { withCredentials: true });
      setProducts(data.products);
      setTotalPages(data.pages);
      setTotal(data.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, filterType, filterCategory, search]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { setPage(1); }, [search, filterType, filterCategory]);

  // Selection
  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelectedIds(selectedIds.size === products.length ? new Set() : new Set(products.map(p => p.id)));

  // Product CRUD
  const openAddDialog = () => {
    setEditingProduct(null); setFormType("Capsules"); setFormIngredient(""); setFormStrength("");
    setFormQty(""); setFormQtyUnit(""); setFormWsp(""); setFormRrp(""); setFormQuoteRequired(false);
    setFormCategories([]);
    setShowProductDialog(true);
  };
  const openEditDialog = (p) => {
    setEditingProduct(p); setFormType(p.type); setFormIngredient(p.ingredient); setFormStrength(p.strength);
    setFormQty(p.qty); setFormQtyUnit(p.qty_unit || ""); setFormWsp(p.wsp?.toString() || ""); setFormRrp(p.rrp?.toString() || "");
    setFormQuoteRequired(p.quote_required); setFormCategories(p.categories || []); setShowProductDialog(true);
  };
  const handleProductSubmit = async () => {
    if (!formIngredient.trim()) { toast.error("Ingredient required"); return; }
    const payload = { type: formType, ingredient: formIngredient, strength: formStrength, qty: formQty, qty_unit: formQtyUnit, wsp: formWsp ? parseFloat(formWsp) : null, rrp: formRrp ? parseFloat(formRrp) : null, quote_required: formQuoteRequired, categories: formCategories };
    try {
      if (editingProduct) { await axios.put(`${API}/products/${editingProduct.id}`, payload, { withCredentials: true }); toast.success("Product updated"); }
      else { await axios.post(`${API}/products`, payload, { withCredentials: true }); toast.success("Product added"); }
      setShowProductDialog(false); fetchProducts();
    } catch { toast.error("Failed to save"); }
  };
  const toggleFormCategory = (key) => {
    setFormCategories(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  const handleDelete = async (p) => {
    if (!window.confirm(`Delete ${p.ingredient} ${p.strength}?`)) return;
    try { await axios.delete(`${API}/products/${p.id}`, { withCredentials: true }); toast.success("Deleted"); fetchProducts(); } catch { toast.error("Failed"); }
  };

  // Bulk price update
  const handleBulkPrice = async () => {
    if (!bulkValue || selectedIds.size === 0) { toast.error("Select products and enter a value"); return; }
    try {
      const { data } = await axios.put(`${API}/products/bulk-price`, { product_ids: Array.from(selectedIds), adjustment_type: bulkType, value: parseFloat(bulkValue), apply_to: bulkApply }, { withCredentials: true });
      toast.success(`${data.updated} products updated`);
      setShowBulkDialog(false); setSelectedIds(new Set()); fetchProducts();
    } catch { toast.error("Failed"); }
  };

  // CSV Export
  const handleExport = () => {
    axios.get(`${API}/products/export/csv`, { withCredentials: true, responseType: "blob" }).then(res => {
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "amcal-price-list.csv"; a.click();
      URL.revokeObjectURL(url); toast.success("Exported");
    }).catch(() => toast.error("Export failed"));
  };

  // CSV Import
  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    const formData = new FormData(); formData.append("file", importFile);
    try {
      const { data } = await axios.post(`${API}/products/import/csv`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Created ${data.created}, updated ${data.updated} products`);
      if (data.errors?.length) toast.error(`${data.errors.length} errors`);
      setShowImportDialog(false); setImportFile(null); fetchProducts();
    } catch { toast.error("Import failed"); }
    setImporting(false);
  };

  // Price History
  const loadHistory = async () => {
    try { const { data } = await axios.get(`${API}/products/price-history?limit=50`, { withCredentials: true }); setHistory(data.history); } catch { /* */ }
    setShowHistoryDialog(true);
  };

  // Categories
  const loadCategories = async () => {
    try { const { data } = await axios.get(`${API}/categories`, { withCredentials: true }); setCategories(data.categories); } catch { /* */ }
    setShowCategoriesDialog(true);
  };
  const addCategory = async () => {
    if (!newCatLabel.trim()) return;
    try { await axios.post(`${API}/categories`, { label: newCatLabel }, { withCredentials: true }); setNewCatLabel(""); loadCategories(); fetchProducts(); toast.success("Category added"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const renameCategory = async (cat) => {
    const newLabel = window.prompt("New label:", cat.label);
    if (!newLabel || newLabel === cat.label) return;
    try { await axios.put(`${API}/categories/${cat.id}`, { label: newLabel }, { withCredentials: true }); loadCategories(); fetchProducts(); toast.success("Renamed"); } catch { toast.error("Failed"); }
  };
  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.label}"? It will be removed from all products and clinic logins.`)) return;
    try { await axios.delete(`${API}/categories/${cat.id}`, { withCredentials: true }); loadCategories(); fetchProducts(); toast.success("Deleted"); } catch { toast.error("Failed"); }
  };

  // Bulk categorize
  const handleBulkCategorize = async () => {
    if (selectedIds.size === 0 || !bulkCatKey) { toast.error("Select products and a category"); return; }
    try {
      const { data } = await axios.post(`${API}/admin/products/bulk-categorize`, { product_ids: Array.from(selectedIds), category_key: bulkCatKey, action: bulkCatAction }, { withCredentials: true });
      toast.success(`${data.modified} products updated`);
      setShowBulkCatDialog(false); setSelectedIds(new Set()); fetchProducts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const catLabel = (key) => categories.find(c => c.key === key)?.label || key;

  // Pricing Health
  const loadHealth = async () => {
    try { const { data } = await axios.get(`${API}/admin/pricing-health`, { withCredentials: true }); setHealth(data); } catch { /* */ }
    setShowHealthDialog(true);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">ADMIN</p>
          <h1 className="font-[Chivo] text-3xl font-black tracking-tighter text-zinc-950 mt-1">Product Management</h1>
          <p className="text-sm text-zinc-500 mt-1">{total} products</p>
        </div>
        <button onClick={openAddDialog} className="bg-[#0055FF] text-white font-semibold px-5 py-2.5 text-sm hover:bg-[#0044CC] transition-colors flex items-center gap-2" data-testid="add-product-button">
          <Plus size={16} weight="bold" /> Add Product
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="product-toolbar">
        <button onClick={() => { if (selectedIds.size === 0) { toast.error("Select products first"); return; } setShowBulkDialog(true); }} className="px-3 py-2 text-xs font-medium bg-zinc-100 border border-zinc-200 text-zinc-700 hover:bg-zinc-200 transition-colors flex items-center gap-1.5" data-testid="bulk-price-button">
          <CurrencyDollar size={14} /> Bulk Price Update {selectedIds.size > 0 && `(${selectedIds.size})`}
        </button>
        <button onClick={() => { if (selectedIds.size === 0) { toast.error("Select products first"); return; } setBulkCatKey(""); setBulkCatAction("add"); setShowBulkCatDialog(true); }} className="px-3 py-2 text-xs font-medium bg-zinc-100 border border-zinc-200 text-zinc-700 hover:bg-zinc-200 transition-colors flex items-center gap-1.5" data-testid="bulk-categorize-button">
          <Tag size={14} /> Bulk Categorize {selectedIds.size > 0 && `(${selectedIds.size})`}
        </button>
        <button onClick={handleExport} className="px-3 py-2 text-xs font-medium bg-zinc-100 border border-zinc-200 text-zinc-700 hover:bg-zinc-200 transition-colors flex items-center gap-1.5" data-testid="export-csv-button">
          <DownloadSimple size={14} /> Export CSV
        </button>
        <button onClick={() => setShowImportDialog(true)} className="px-3 py-2 text-xs font-medium bg-zinc-100 border border-zinc-200 text-zinc-700 hover:bg-zinc-200 transition-colors flex items-center gap-1.5" data-testid="import-csv-button">
          <Upload size={14} /> Import CSV
        </button>
        <button onClick={loadHistory} className="px-3 py-2 text-xs font-medium bg-zinc-100 border border-zinc-200 text-zinc-700 hover:bg-zinc-200 transition-colors flex items-center gap-1.5" data-testid="price-history-button">
          <Clock size={14} /> Price History
        </button>
        <button onClick={loadCategories} className="px-3 py-2 text-xs font-medium bg-zinc-100 border border-zinc-200 text-zinc-700 hover:bg-zinc-200 transition-colors flex items-center gap-1.5" data-testid="categories-button">
          <Tag size={14} /> Categories
        </button>
        <button onClick={loadHealth} className="px-3 py-2 text-xs font-medium bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 transition-colors flex items-center gap-1.5" data-testid="pricing-health-button">
          <Warning size={14} /> Pricing Health
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-none" data-testid="admin-product-search" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full sm:w-48 rounded-none" data-testid="admin-product-type-filter"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {(types.length > 0 ? types : TYPES).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-full sm:w-56 rounded-none" data-testid="admin-product-category-filter"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Products Table */}
      <div className="border border-zinc-200 overflow-x-auto" data-testid="admin-products-table">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr>
              <th className="border-b-2 border-zinc-950 py-3 px-2 w-10"><Checkbox checked={selectedIds.size === products.length && products.length > 0} onCheckedChange={toggleAll} /></th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Type</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Ingredient</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Categories</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Strength</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Qty</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">WSP</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">RRP</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500 w-20">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="py-12 text-center text-sm text-zinc-500 font-mono">LOADING...</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={9} className="py-12 text-center text-sm text-zinc-500">No products found</td></tr>
            ) : products.map((p, i) => (
              <tr key={p.id || i} className="border-b border-zinc-200 hover:bg-zinc-50 transition-colors" data-testid={`admin-product-row-${i}`}>
                <td className="py-3 px-2"><Checkbox checked={selectedIds.has(p.id)} onCheckedChange={() => toggleSelect(p.id)} /></td>
                <td className="py-3 px-4 text-sm text-zinc-500">{p.type}</td>
                <td className="py-3 px-4 text-sm text-zinc-800 font-medium">{p.ingredient}</td>
                <td className="py-3 px-4">
                  <div className="flex flex-wrap gap-1">
                    {(p.categories || []).map(k => (
                      <span key={k} className="inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-zinc-100 text-zinc-600 border border-zinc-200" data-testid={`admin-cat-${p.id}-${k}`}>{catLabel(k)}</span>
                    ))}
                    {(!p.categories || p.categories.length === 0) && <span className="text-xs text-zinc-400">—</span>}
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-zinc-600 font-mono">{p.strength}</td>
                <td className="py-3 px-4 text-sm text-zinc-600">{p.qty}</td>
                <td className="py-3 px-4 text-sm font-semibold">{p.quote_required ? <span className="text-amber-600">Quote</span> : p.wsp != null ? `$${p.wsp.toFixed(2)}` : "-"}</td>
                <td className="py-3 px-4 text-sm font-semibold">{p.quote_required ? <span className="text-amber-600">Quote</span> : p.rrp != null ? `$${p.rrp.toFixed(2)}` : "-"}</td>
                <td className="py-3 px-4">
                  <div className="flex gap-1">
                    <button onClick={() => openEditDialog(p)} className="p-1.5 text-zinc-400 hover:text-[#0055FF]"><PencilSimple size={16} /></button>
                    <button onClick={() => handleDelete(p)} className="p-1.5 text-zinc-400 hover:text-red-500"><Trash size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-zinc-500">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 text-sm border border-zinc-200 hover:bg-zinc-50 disabled:opacity-50">Previous</button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 text-sm border border-zinc-200 hover:bg-zinc-50 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      {/* Add/Edit Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="sm:max-w-lg"><DialogHeader><DialogTitle className="font-[Chivo]">{editingProduct ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Type</Label><Select value={formType} onValueChange={setFormType}><SelectTrigger className="mt-1.5 rounded-none"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Ingredient *</Label><Input value={formIngredient} onChange={e => setFormIngredient(e.target.value)} className="mt-1.5 rounded-none" /></div>
            <div className="grid grid-cols-2 gap-3"><div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Strength</Label><Input value={formStrength} onChange={e => setFormStrength(e.target.value)} className="mt-1.5 rounded-none" /></div><div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Quantity</Label><Input value={formQty} onChange={e => setFormQty(e.target.value)} className="mt-1.5 rounded-none" /></div></div>
            <div className="grid grid-cols-2 gap-3"><div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">WSP ($)</Label><Input type="number" step="0.01" value={formWsp} onChange={e => setFormWsp(e.target.value)} className="mt-1.5 rounded-none" disabled={formQuoteRequired} /></div><div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">RRP ($)</Label><Input type="number" step="0.01" value={formRrp} onChange={e => setFormRrp(e.target.value)} className="mt-1.5 rounded-none" disabled={formQuoteRequired} /></div></div>
            <div className="flex items-center gap-2"><Checkbox checked={formQuoteRequired} onCheckedChange={c => { setFormQuoteRequired(c); if (c) { setFormWsp(""); setFormRrp(""); } }} /><Label className="text-sm text-zinc-700">Quote required</Label></div>
            <div data-testid="form-categories">
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Categories</Label>
              <p className="text-xs text-zinc-400 mt-1 mb-2">Select all clinical categories this product belongs to. Untagged products are visible to everyone.</p>
              {categories.length === 0 ? <p className="text-xs text-zinc-400">No categories defined yet — open Categories dialog to add.</p> : (
                <div className="grid grid-cols-2 gap-2">
                  {categories.map(c => (
                    <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 border border-zinc-200 cursor-pointer hover:bg-zinc-50 transition-colors" data-testid={`form-cat-${c.key}`}>
                      <Checkbox checked={formCategories.includes(c.key)} onCheckedChange={() => toggleFormCategory(c.key)} />
                      <span className="text-xs text-zinc-700">{c.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <button onClick={handleProductSubmit} className="w-full bg-[#0055FF] text-white font-semibold py-3 hover:bg-[#0044CC] transition-colors">{editingProduct ? "UPDATE" : "ADD PRODUCT"}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Price Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="sm:max-w-md" data-testid="bulk-price-dialog"><DialogHeader><DialogTitle className="font-[Chivo]">Bulk Price Update</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">{selectedIds.size} product(s) selected</p>
            <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Adjustment Type</Label>
              <Select value={bulkType} onValueChange={setBulkType}><SelectTrigger className="mt-1.5 rounded-none"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="percentage">Percentage (%)</SelectItem><SelectItem value="flat">Flat Amount ($)</SelectItem></SelectContent></Select></div>
            <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">{bulkType === "percentage" ? "Percentage (e.g. 5 for +5%, -10 for -10%)" : "Amount (e.g. 2.50 for +$2.50)"}</Label>
              <Input type="number" step="0.01" value={bulkValue} onChange={e => setBulkValue(e.target.value)} className="mt-1.5 rounded-none" data-testid="bulk-value-input" placeholder={bulkType === "percentage" ? "5" : "2.50"} /></div>
            <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Apply To</Label>
              <Select value={bulkApply} onValueChange={setBulkApply}><SelectTrigger className="mt-1.5 rounded-none"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="both">Both WSP & RRP</SelectItem><SelectItem value="wsp">WSP Only</SelectItem><SelectItem value="rrp">RRP Only</SelectItem></SelectContent></Select></div>
            <button onClick={handleBulkPrice} className="w-full bg-[#0055FF] text-white font-semibold py-3 hover:bg-[#0044CC] transition-colors" data-testid="apply-bulk-price">APPLY PRICE CHANGE</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md" data-testid="import-csv-dialog"><DialogHeader><DialogTitle className="font-[Chivo]">Import Price List (CSV)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-zinc-50 p-3 border border-zinc-200 text-xs text-zinc-600 space-y-1">
              <p className="font-mono font-bold text-zinc-800">CSV Format:</p>
              <p>type, ingredient, strength, qty, qty_unit, wsp, rrp, quote_required</p>
              <p className="text-zinc-400 mt-1">Matching products (same type+ingredient+strength+qty) will be updated. New ones will be created.</p>
            </div>
            <label className={`border-2 border-dashed p-8 text-center cursor-pointer block transition-colors ${importFile ? "border-emerald-400 bg-emerald-50/30" : "border-zinc-300 hover:border-[#0055FF]"}`}>
              {importFile ? <p className="text-sm font-medium text-zinc-800">{importFile.name}</p> : <><Upload size={24} className="mx-auto text-zinc-400 mb-2" /><p className="text-sm text-zinc-600">Click to upload CSV</p></>}
              <input type="file" accept=".csv" onChange={e => setImportFile(e.target.files?.[0] || null)} className="hidden" />
            </label>
            <button onClick={handleImport} disabled={!importFile || importing} className="w-full bg-[#0055FF] text-white font-semibold py-3 hover:bg-[#0044CC] transition-colors disabled:opacity-50">{importing ? "IMPORTING..." : "IMPORT"}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Price History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="price-history-dialog"><DialogHeader><DialogTitle className="font-[Chivo]">Price Change History</DialogTitle></DialogHeader>
          {history.length === 0 ? <p className="text-sm text-zinc-500 py-8 text-center">No price changes recorded yet</p> : (
            <div className="border border-zinc-200">
              <table className="w-full border-collapse text-left text-sm">
                <thead><tr>
                  <th className="border-b border-zinc-300 py-2 px-3 font-mono text-xs text-zinc-500">Date</th>
                  <th className="border-b border-zinc-300 py-2 px-3 font-mono text-xs text-zinc-500">Product</th>
                  <th className="border-b border-zinc-300 py-2 px-3 font-mono text-xs text-zinc-500">Field</th>
                  <th className="border-b border-zinc-300 py-2 px-3 font-mono text-xs text-zinc-500">Old</th>
                  <th className="border-b border-zinc-300 py-2 px-3 font-mono text-xs text-zinc-500">New</th>
                  <th className="border-b border-zinc-300 py-2 px-3 font-mono text-xs text-zinc-500">By</th>
                  <th className="border-b border-zinc-300 py-2 px-3 font-mono text-xs text-zinc-500">Type</th>
                </tr></thead>
                <tbody>{history.map((h, i) => (
                  <tr key={i} className="border-b border-zinc-100">
                    <td className="py-2 px-3 text-zinc-400 text-xs">{new Date(h.created_at).toLocaleString()}</td>
                    <td className="py-2 px-3 text-zinc-800">{h.product_name || h.product_id?.slice(0, 8)}</td>
                    <td className="py-2 px-3 font-mono text-xs uppercase text-zinc-500">{h.field}</td>
                    <td className="py-2 px-3 font-mono text-red-600">{h.old_value != null ? `$${Number(h.old_value).toFixed(2)}` : "-"}</td>
                    <td className="py-2 px-3 font-mono text-emerald-600">{h.new_value != null ? `$${Number(h.new_value).toFixed(2)}` : "-"}</td>
                    <td className="py-2 px-3 text-zinc-500">{h.changed_by_name || "System"}</td>
                    <td className="py-2 px-3 text-xs text-zinc-400">{h.change_type}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Categories Dialog */}
      <Dialog open={showCategoriesDialog} onOpenChange={setShowCategoriesDialog}>
        <DialogContent className="sm:max-w-md" data-testid="categories-dialog"><DialogHeader><DialogTitle className="font-[Chivo]">Manage Clinical Categories</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-zinc-500">e.g. Simple Compounding, Vet, Dental. Used to control which clinics see which products.</p>
            <div className="flex gap-2">
              <Input value={newCatLabel} onChange={e => setNewCatLabel(e.target.value)} placeholder="New category label..." className="rounded-none flex-1" data-testid="new-category-input" />
              <button onClick={addCategory} className="bg-[#0055FF] text-white px-4 py-2 text-sm font-semibold hover:bg-[#0044CC]" data-testid="add-category-button">Add</button>
            </div>
            <div className="border border-zinc-200 divide-y divide-zinc-100 max-h-[300px] overflow-y-auto">
              {categories.map(cat => (
                <div key={cat.id} className="flex items-center justify-between px-3 py-2" data-testid={`cat-row-${cat.key}`}>
                  <div>
                    <p className="text-sm text-zinc-800">{cat.label}</p>
                    <p className="font-mono text-[10px] text-zinc-400 uppercase tracking-wider">{cat.key}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => renameCategory(cat)} className="p-1 text-zinc-400 hover:text-[#0055FF]" data-testid={`rename-cat-${cat.key}`}><PencilSimple size={14} /></button>
                    <button onClick={() => deleteCategory(cat)} className="p-1 text-zinc-400 hover:text-red-500" data-testid={`delete-cat-${cat.key}`}><Trash size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Categorize Dialog */}
      <Dialog open={showBulkCatDialog} onOpenChange={setShowBulkCatDialog}>
        <DialogContent className="sm:max-w-md" data-testid="bulk-categorize-dialog"><DialogHeader><DialogTitle className="font-[Chivo]">Bulk Categorize</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">{selectedIds.size} product(s) selected</p>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Action</Label>
              <Select value={bulkCatAction} onValueChange={setBulkCatAction}>
                <SelectTrigger className="mt-1.5 rounded-none" data-testid="bulk-cat-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Add Category</SelectItem>
                  <SelectItem value="remove">Remove Category</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Category</Label>
              <Select value={bulkCatKey} onValueChange={setBulkCatKey}>
                <SelectTrigger className="mt-1.5 rounded-none" data-testid="bulk-cat-select"><SelectValue placeholder="Choose category" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <button onClick={handleBulkCategorize} disabled={!bulkCatKey} className="w-full bg-[#0055FF] text-white font-semibold py-3 hover:bg-[#0044CC] transition-colors disabled:opacity-50" data-testid="apply-bulk-categorize">APPLY</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pricing Health Dialog */}
      <Dialog open={showHealthDialog} onOpenChange={setShowHealthDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="pricing-health-dialog"><DialogHeader><DialogTitle className="font-[Chivo]">Pricing Health Check</DialogTitle></DialogHeader>
          {!health ? <p className="text-sm text-zinc-500 py-8 text-center">Loading...</p> : (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-0 border border-zinc-200">
                <div className="p-4 border-r border-zinc-200"><p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Priced</p><p className="font-[Chivo] text-2xl font-black text-emerald-600 mt-1">{health.priced_products}</p></div>
                <div className="p-4 border-r border-zinc-200"><p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Quote Only</p><p className="font-[Chivo] text-2xl font-black text-amber-600 mt-1">{health.quote_required}</p></div>
                <div className="p-4"><p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Total</p><p className="font-[Chivo] text-2xl font-black text-zinc-950 mt-1">{health.total_products}</p></div>
              </div>
              {/* Low Margin */}
              {health.low_margin_products?.length > 0 && (
                <div>
                  <h3 className="font-[Chivo] font-bold text-red-600 mb-2 flex items-center gap-2"><Warning size={16} /> Low Margin Products ({"<"}10%)</h3>
                  <div className="border border-red-200 divide-y divide-red-100">
                    {health.low_margin_products.map((p, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div><span className="font-medium text-zinc-800">{p.ingredient}</span> <span className="text-zinc-400">{p.strength} {p.qty}</span></div>
                        <div className="flex gap-4 font-mono text-xs">
                          <span className="text-zinc-500">WSP ${p.wsp?.toFixed(2)}</span>
                          <span className="text-zinc-500">RRP ${p.rrp?.toFixed(2)}</span>
                          <span className="text-red-600 font-bold">{p.margin_pct}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {health.low_margin_products?.length === 0 && <p className="text-sm text-emerald-600 font-medium">All products have healthy margins (10%+)</p>}
              {/* Missing Prices */}
              {health.missing_wsp?.length > 0 && (
                <div>
                  <h3 className="font-[Chivo] font-bold text-amber-600 mb-2">Missing WSP ({health.missing_wsp.length})</h3>
                  <div className="border border-amber-200 divide-y divide-amber-100 max-h-[150px] overflow-y-auto">
                    {health.missing_wsp.map((p, i) => <div key={i} className="px-3 py-1.5 text-sm text-zinc-700">{p.ingredient} {p.strength} {p.qty}</div>)}
                  </div>
                </div>
              )}
              {health.missing_rrp?.length > 0 && (
                <div>
                  <h3 className="font-[Chivo] font-bold text-amber-600 mb-2">Missing RRP ({health.missing_rrp.length})</h3>
                  <div className="border border-amber-200 divide-y divide-amber-100 max-h-[150px] overflow-y-auto">
                    {health.missing_rrp.map((p, i) => <div key={i} className="px-3 py-1.5 text-sm text-zinc-700">{p.ingredient} {p.strength} {p.qty}</div>)}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
